import {
  operatorCategories,
  operatorsByCategory,
  aggregationOperators,
  conditionalKeywords,
  comparisonOperators,
  logicalOperators,
} from "@/src/constants/formulaConstants.js";
import Quill from "quill";

const Embed = Quill.import("blots/embed");

class FormulaOperatorBlot extends Embed {
  static create(value) {
    if (!value || !value.operator) {
      return null;
    }

    const node = super.create();
    node.classList.add("formula_operator", "space_mono");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-type", "operator");
    node.setAttribute("data-operator", value.operator);
    node.setAttribute("data-label", value.label || value.operator);

    node.textContent = value.label || value.operator;
    return node;
  }

  static value(node) {
    const operator = node.getAttribute("data-operator");
    if (!operator) {
      return null;
    }

    return {
      operator: operator,
      label: node.textContent,
    };
  }
}

FormulaOperatorBlot.blotName = "formula-operator";
FormulaOperatorBlot.tagName = "span";
FormulaOperatorBlot.className = "formula_operator";

class FormulaPropertyBlot extends Embed {
  static create(value) {
    if (!value || !value.id) {
      return null;
    }

    const node = super.create();
    node.classList.add("formula_property", "tag_css");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-type", "attribute");
    node.setAttribute("data-id", value.id);
    node.setAttribute("data-name", value.name || value.id);

    node.textContent = value.name || value.id;
    return node;
  }

  static value(node) {
    const id = node.getAttribute("data-id");
    if (!id) {
      return null;
    }

    return {
      id: id,
      name: node.getAttribute("data-name") || id,
    };
  }
}

FormulaPropertyBlot.blotName = "formula-property";
FormulaPropertyBlot.tagName = "span";
FormulaPropertyBlot.className = "formula_property";

class FormulaKeywordBlot extends Embed {
  static create(value) {
    if (!value || !value.keyword) {
      return null;
    }

    const node = super.create();
    node.classList.add("formula_keyword", "space_mono");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-type", "keyword");
    node.setAttribute("data-keyword", value.keyword);

    node.textContent = value.keyword;
    return node;
  }

  static value(node) {
    const keyword = node.getAttribute("data-keyword");
    if (!keyword) {
      return null;
    }

    return {
      keyword: keyword,
    };
  }
}

FormulaKeywordBlot.blotName = "formula-keyword";
FormulaKeywordBlot.tagName = "span";
FormulaKeywordBlot.className = "formula_keyword";

class FormulaComparisonBlot extends Embed {
  static create(value) {
    if (!value || !value.operator) {
      return null;
    }

    const node = super.create();
    node.classList.add("formula_comparison", "space_mono");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-type", "comparison");
    node.setAttribute("data-operator", value.operator);

    node.textContent = value.operator;
    return node;
  }

  static value(node) {
    const operator = node.getAttribute("data-operator");
    if (!operator) {
      return null;
    }

    return {
      operator: operator,
    };
  }
}

FormulaComparisonBlot.blotName = "formula-comparison";
FormulaComparisonBlot.tagName = "span";
FormulaComparisonBlot.className = "formula_comparison";

class FormulaStringBlot extends Embed {
  static create(value) {
    if (!value || value.value === undefined) {
      return null;
    }

    const node = super.create();
    node.classList.add("formula_string", "space_mono");
    node.setAttribute("contenteditable", "false");
    node.setAttribute("data-type", "string");
    node.setAttribute("data-value", value.value);

    node.textContent = `"${value.value}"`;
    return node;
  }

  static value(node) {
    const value = node.getAttribute("data-value");
    if (value === null) {
      return null;
    }

    return {
      value: value,
    };
  }
}

FormulaStringBlot.blotName = "formula-string";
FormulaStringBlot.tagName = "span";
FormulaStringBlot.className = "formula_string";

export const registerFormulaBlots = () => {
  Quill.register(FormulaOperatorBlot, true);
  Quill.register(FormulaPropertyBlot, true);
  Quill.register(FormulaKeywordBlot, true);
  Quill.register(FormulaComparisonBlot, true);
  Quill.register(FormulaStringBlot, true);
};

export const getOperatorLabel = (operator) => {
  for (const category of Object.values(operatorCategories)) {
    const operatorInfo = operatorsByCategory[category.id]?.find(
      (op) => op.value === operator,
    );
    if (operatorInfo) {
      return operatorInfo.label;
    }
  }
  return operator;
};

export const setupFormulaKeyboardHandlers = (quill) => {
  quill.root.addEventListener("keydown", (e) => {
    const arithmeticKeys = ["+", "-", "*", "/"];
    const comparisonKeys = ["=", ">", "<"];

    if (arithmeticKeys.includes(e.key)) {
      e.preventDefault();

      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength() - 1;

      quill.insertEmbed(index, "formula-operator", {
        operator: e.key,
        label: getOperatorLabel(e.key),
      });

      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);

      return;
    }

    if (comparisonKeys.includes(e.key)) {
      e.preventDefault();

      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength() - 1;

      if (e.key === "=" && index >= 2) {
        const [prevLeaf] = quill.getLeaf(index - 2);
        if (prevLeaf?.domNode?.classList?.contains("formula_comparison")) {
          const prevOp = prevLeaf.domNode.getAttribute("data-operator");
          if (prevOp === ">" || prevOp === "<") {
            const blotIndex = quill.getIndex(prevLeaf);
            quill.deleteText(blotIndex, index - blotIndex);
            quill.insertEmbed(blotIndex, "formula-comparison", { operator: prevOp + "=" });
            quill.insertText(blotIndex + 1, " ");
            quill.setSelection(blotIndex + 2, 0);
            return;
          }
        }
      }

      quill.insertEmbed(index, "formula-comparison", {
        operator: e.key,
      });

      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);

      return;
    }

    if (e.key === "!") {
      e.preventDefault();

      const selection = quill.getSelection(true);
      const index = selection ? selection.index : quill.getLength() - 1;

      quill.insertEmbed(index, "formula-comparison", {
        operator: "!=",
      });

      quill.insertText(index + 1, " ");
      quill.setSelection(index + 2, 0);

      return;
    }

    if (e.key === "Delete") {
      const selection = quill.getSelection();
      if (!selection) return;

      const index = selection.index;
      const length = quill.getLength();

      if (index >= length - 1) return;

      try {
        const [leaf] = quill.getLeaf(index);
        if (!leaf || !leaf.domNode) return;

        const isOperator = leaf.domNode.classList?.contains("formula_operator");
        const isProperty = leaf.domNode.classList?.contains("formula_property");
        const isKeyword = leaf.domNode.classList?.contains("formula_keyword");
        const isComparison = leaf.domNode.classList?.contains("formula_comparison");
        const isString = leaf.domNode.classList?.contains("formula_string");

        if (isOperator || isProperty || isKeyword || isComparison || isString) {
          e.preventDefault();

          const blotIndex = quill.getIndex(leaf);
          quill.deleteText(blotIndex, 1);
          quill.setSelection(blotIndex, 0);
        }
      } catch (error) {
        console.error("Error in Delete key handler:", error);
      }
    }

    if (e.key === "Backspace") {
      const selection = quill.getSelection();
      if (!selection || selection.index <= 0) return;

      const index = selection.index;

      try {
        const [prevLeaf] = quill.getLeaf(index - 1);
        if (!prevLeaf || !prevLeaf.domNode) return;

        const isOperator =
          prevLeaf.domNode.classList?.contains("formula_operator");
        const isProperty =
          prevLeaf.domNode.classList?.contains("formula_property");
        const isKeyword =
          prevLeaf.domNode.classList?.contains("formula_keyword");
        const isComparison =
          prevLeaf.domNode.classList?.contains("formula_comparison");
        const isString =
          prevLeaf.domNode.classList?.contains("formula_string");

        if (!isOperator && !isProperty && !isKeyword && !isComparison && !isString) return;

        try {
          const blotIndex = quill.getIndex(prevLeaf);

          if (index !== blotIndex + 1) return;

          e.preventDefault();
          quill.deleteText(blotIndex, 1);
          quill.setSelection(blotIndex, 0);
        } catch (error) {
          console.error("Error deleting formula blot:", error);
        }
      } catch (error) {
        console.error("Error in Backspace handler:", error);
      }
    }
  });
};

export const setupFormulaClickHandlers = (quill) => {
  let isMouseDown = false;
  let startSelection = null;
  let isDragging = false;
  let dragStartPos = { x: 0, y: 0 };

  quill.root.addEventListener("mousedown", (e) => {
    isMouseDown = true;
    isDragging = false;
    dragStartPos = { x: e.clientX, y: e.clientY };
    startSelection = quill.getSelection();
  });

  quill.root.addEventListener("mousemove", (e) => {
    if (isMouseDown) {
      const distance = Math.sqrt(
        Math.pow(e.clientX - dragStartPos.x, 2) +
          Math.pow(e.clientY - dragStartPos.y, 2),
      );

      if (distance > 3) {
        isDragging = true;
      }
    }
  });

  quill.root.addEventListener("mouseup", () => {
    isMouseDown = false;
    setTimeout(() => {
      isDragging = false;
    }, 10);
  });

  quill.root.addEventListener("click", (e) => {
    if (isDragging) return;

    const currentSelection = quill.getSelection();
    if (currentSelection && currentSelection.length > 0) return;

    let target = e.target;

    const isOperator = target.classList?.contains("formula_operator");
    const isProperty = target.classList?.contains("formula_property");
    const isKeyword = target.classList?.contains("formula_keyword");
    const isComparison = target.classList?.contains("formula_comparison");
    const isString = target.classList?.contains("formula_string");

    if (isOperator || isProperty || isKeyword || isComparison || isString) {
      e.preventDefault();
      e.stopPropagation();

      try {
        const blot = Quill.find(target);
        if (blot) {
          const index = quill.getIndex(blot);
          quill.setSelection(index, 1);
        }
      } catch (error) {
        console.error("Error selecting formula blot:", error);
      }
    } else if (target === quill.root) {
      const rect = quill.root.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const blotNodes = quill.root.querySelectorAll(
        ".formula_operator, .formula_property, .formula_keyword, .formula_comparison, .formula_string",
      );

      let closestBlotNode = null;
      let minDistance = Infinity;

      blotNodes.forEach((blotNode) => {
        const blotRect = blotNode.getBoundingClientRect();
        const rightEdge = blotRect.right - rect.left;
        const blotCenter = blotRect.top + blotRect.height / 2 - rect.top;

        if (
          x >= rightEdge &&
          x <= rightEdge + 20 &&
          Math.abs(y - blotCenter) < 20
        ) {
          const distance = Math.abs(x - rightEdge) + Math.abs(y - blotCenter);
          if (distance < minDistance) {
            minDistance = distance;
            closestBlotNode = blotNode;
          }
        }
      });

      if (closestBlotNode && minDistance < 30) {
        try {
          const blot = Quill.find(closestBlotNode);
          if (blot) {
            const index = quill.getIndex(blot);
            quill.setSelection(index + 1, 0);
            e.preventDefault();
            e.stopPropagation();
          }
        } catch (error) {
          console.error("Error positioning cursor:", error);
        }
      }
    }
  });
};

export const setupFormulaMutationObserver = (quill) => {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === "childList") {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const isOperator = node.classList?.contains("formula_operator");
            const isProperty = node.classList?.contains("formula_property");
            const isKeyword = node.classList?.contains("formula_keyword");
            const isComparison = node.classList?.contains("formula_comparison");
            const isString = node.classList?.contains("formula_string");

            if (isOperator || isProperty || isKeyword || isComparison || isString) {
              node.setAttribute("contenteditable", "false");
            }

            node
              .querySelectorAll(".formula_operator, .formula_property, .formula_keyword, .formula_comparison, .formula_string")
              .forEach((blotNode) => {
                blotNode.setAttribute("contenteditable", "false");
              });
          }
        });
      }
    });
  });

  observer.observe(quill.root, { childList: true, subtree: true });

  return observer;
};

export const parseQuillDeltaToElements = (quill) => {
  const elements = [];
  const delta = quill.getContents();

  delta.ops.forEach((op) => {
    if (typeof op.insert === "string") {
      const text = op.insert.trim();
      if (text) {
        let i = 0;
        while (i < text.length) {
          const char = text[i];

          if (char === "(" || char === ")") {
            elements.push({ type: "bracket", bracket: char });
            i++;
          } else if (char === " ") {
            i++;
          } else if (char === ",") {
            elements.push({ type: "comma" });
            i++;
          } else {
            let numStr = "";
            while (
              i < text.length &&
              ((text[i] >= "0" && text[i] <= "9") ||
                text[i] === "." ||
                text[i] === "-")
            ) {
              numStr += text[i];
              i++;
            }
            if (numStr) {
              const num = parseFloat(numStr);
              if (!isNaN(num)) {
                elements.push({
                  type: "literal",
                  value: num,
                  data_type: "number",
                });
              }
            } else {
              i++;
            }
          }
        }
      }
    } else if (op.insert?.["formula-operator"]) {
      const value = op.insert["formula-operator"];
      elements.push({
        type: "operator",
        operator: value.operator,
      });
    } else if (op.insert?.["formula-property"]) {
      const value = op.insert["formula-property"];
      elements.push({
        type: "attribute",
        id: value.id,
      });
    } else if (op.insert?.["formula-keyword"]) {
      const value = op.insert["formula-keyword"];
      elements.push({
        type: "keyword",
        keyword: value.keyword,
      });
    } else if (op.insert?.["formula-comparison"]) {
      const value = op.insert["formula-comparison"];
      elements.push({
        type: "comparison",
        operator: value.operator,
      });
    } else if (op.insert?.["formula-string"]) {
      const value = op.insert["formula-string"];
      elements.push({
        type: "string",
        value: value.value,
      });
    }
  });

  return elements;
};

export const buildFormulaString = (quill) => {
  const delta = quill.getContents();
  let formula = "";

  delta.ops.forEach((op) => {
    if (typeof op.insert === "string") {
      const text = op.insert.replace(/\n/g, "");
      formula += text;
    } else if (op.insert?.["formula-operator"]) {
      formula += ` ${op.insert["formula-operator"].operator} `;
    } else if (op.insert?.["formula-property"]) {
      formula += `{${op.insert["formula-property"].id}}`;
    } else if (op.insert?.["formula-keyword"]) {
      formula += ` ${op.insert["formula-keyword"].keyword} `;
    } else if (op.insert?.["formula-comparison"]) {
      formula += ` ${op.insert["formula-comparison"].operator} `;
    } else if (op.insert?.["formula-string"]) {
      formula += `"${op.insert["formula-string"].value}"`;
    }
  });

  return formula.replace(/\s+/g, " ").trim();
};

export const parseFormulaString = (formula, getAttributeById) => {
  const elements = [];
  let i = 0;

  const allKeywords = [...conditionalKeywords, ...logicalOperators];

  while (i < formula.length) {
    const char = formula[i];

    if (char === " ") {
      i++;
      continue;
    }

    if (char === "{") {
      const endBrace = formula.indexOf("}", i);
      if (endBrace !== -1) {
        const id = formula.substring(i + 1, endBrace);
        const attr = getAttributeById(id);
        elements.push({
          type: "attribute",
          id: id,
          name: attr?.name || id,
        });
        i = endBrace + 1;
        continue;
      }
    }

    if (char === '"') {
      const endQuote = formula.indexOf('"', i + 1);
      if (endQuote !== -1) {
        const stringValue = formula.substring(i + 1, endQuote);
        elements.push({
          type: "string",
          value: stringValue,
        });
        i = endQuote + 1;
        continue;
      }
    }

    if (char === ",") {
      elements.push({ type: "comma" });
      i++;
      continue;
    }

    const twoCharOp = formula.substring(i, i + 2);
    if (["!=", ">=", "<="].includes(twoCharOp)) {
      elements.push({
        type: "comparison",
        operator: twoCharOp,
      });
      i += 2;
      continue;
    }

    if (["=", ">", "<"].includes(char)) {
      elements.push({
        type: "comparison",
        operator: char,
      });
      i++;
      continue;
    }

    if (["+", "-", "*", "/"].includes(char)) {
      elements.push({
        type: "operator",
        operator: char,
      });
      i++;
      continue;
    }

    if (char === "(" || char === ")") {
      elements.push({ type: "bracket", bracket: char });
      i++;
      continue;
    }

    let matched = false;

    for (const keyword of allKeywords) {
      const remaining = formula.substring(i).toUpperCase();
      if (remaining.startsWith(keyword)) {
        const nextChar = formula[i + keyword.length];
        if (!nextChar || !/[a-zA-Z0-9_]/.test(nextChar)) {
          elements.push({
            type: "keyword",
            keyword: keyword,
          });
          i += keyword.length;
          matched = true;
          break;
        }
      }
    }
    if (matched) continue;

    for (const op of aggregationOperators) {
      const remaining = formula.substring(i).toLowerCase();
      if (remaining.startsWith(op)) {
        const nextChar = formula[i + op.length];
        if (!nextChar || !/[a-zA-Z0-9_]/.test(nextChar)) {
          elements.push({
            type: "aggregation",
            operator: op,
          });
          i += op.length;
          matched = true;
          break;
        }
      }
    }
    if (matched) continue;

    let numStr = "";
    while (
      i < formula.length &&
      ((formula[i] >= "0" && formula[i] <= "9") ||
        formula[i] === "." ||
        (formula[i] === "-" && numStr === ""))
    ) {
      numStr += formula[i];
      i++;
    }
    if (numStr && !isNaN(parseFloat(numStr))) {
      elements.push({
        type: "literal",
        value: parseFloat(numStr),
      });
      continue;
    }

    if (!matched && numStr === "") {
      i++;
    }
  }

  return elements;
};

const getOperatorPrecedence = (operator) => {
  if (operator === "*" || operator === "/") return 2;
  if (operator === "+" || operator === "-") return 1;
  return 0;
};

export const buildASTFromElements = (elements) => {
  if (elements.length === 0) return null;

  let position = 0;

  const parseExpression = (minPrecedence = 0) => {
    let left = parsePrimary();
    if (!left) return null;

    while (position < elements.length) {
      const current = elements[position];

      if (current?.type !== "operator") break;

      const precedence = getOperatorPrecedence(current.operator);
      if (precedence < minPrecedence) break;

      position++;
      const right = parseExpression(precedence + 1);

      if (!right) break;

      left = {
        type: "operator",
        operator: current.operator,
        operands: [left, right],
      };
    }

    return left;
  };

  const parsePrimary = () => {
    if (position >= elements.length) return null;

    const current = elements[position];

    if (current?.type === "bracket" && current.bracket === "(") {
      position++;
      const expr = parseExpression(0);
      if (
        elements[position]?.type === "bracket" &&
        elements[position].bracket === ")"
      ) {
        position++;
      }
      return expr;
    }

    if (current?.type === "operator") {
      position++;
      const operand = parsePrimary();
      if (operand) {
        return {
          type: "operator",
          operator: current.operator,
          operands: [operand],
        };
      }
      return null;
    }

    if (current?.type === "literal" || current?.type === "attribute") {
      position++;
      return current;
    }

    return null;
  };

  return parseExpression();
};

export const parseASTToElements = (node, elements = []) => {
  if (!node) return elements;

  if (node.type === "operator") {
    if (node.operands.length === 1) {
      elements.push({ type: "operator", operator: node.operator });
      parseASTToElements(node.operands[0], elements);
    } else {
      elements.push({ type: "bracket", bracket: "(" });
      parseASTToElements(node.operands[0], elements);
      elements.push({ type: "operator", operator: node.operator });
      parseASTToElements(node.operands[1], elements);
      elements.push({ type: "bracket", bracket: ")" });
    }
  } else if (node.type === "attribute") {
    elements.push({ type: "attribute", id: node.id || node.slug });
  } else if (node.type === "literal") {
    elements.push({
      type: "literal",
      value: node.value,
      data_type: node.data_type,
    });
  }

  return elements;
};

export {
  FormulaOperatorBlot,
  FormulaPropertyBlot,
  FormulaKeywordBlot,
  FormulaComparisonBlot,
  FormulaStringBlot,
};
